/* eslint-disable react/display-name */
import React, { useState, forwardRef, useImperativeHandle, useEffect, useMemo } from 'react';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// Third Party Libraries
import DatePicker from '~/components/General/OlcDatePicker';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Action
import { SET_ORDERS_FILTERS, CLEAR_ORDERS_FILTERS } from '~/redux/actions/action-types';
import { failure } from '~/redux/actions/snackbar-actions';
import { fetchCampaigns } from '~/redux/actions/campaign-actions';
import { getAllTemplates } from '~/redux/actions/template-builder';

// Utils
import { MESSAGES } from '~/utils/message';
import {
  DATE_REGEX,
  DEFAULT_PAGINATION,
  DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE,
} from '~/utils/constants';
import {
  dateProtector,
  handleRawChangeForDatePicker,
  formatDateOnly,
  parseLocalDateOnly,
} from '~/utils/date-format';

// Components
import MultiSelectNew from '~/components/General/MultiSelectNew';
import GenericAutocomplete from '~/components/General/Autocomplete';

// MUI Components
import IconButton from '@mui/material/IconButton';
import { Close as CloseIcon } from '@mui/icons-material';
import { Box, Grid, InputLabel, Input, InputAdornment } from '@mui/material';

// images
import { MultiSelectArrowCIcon } from '~/assets/images';

// styles
import './styles.scss';

const AdvanceFilters = forwardRef((props, ref) => {
  const { handleCloseFilter, open, updatePagination } = props;
  const [selectedCreatedBy, setSelectedCreatedBy] = useState([]);
  const [selectedProductType, setSelectedProductType] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState([]);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState([]);
  const [createdDate, setCreatedDate] = useState([null, null]);
  const [mailingDate, setMailingDate] = useState([null, null]);
  const [mailedDate, setMailedDate] = useState([null, null]);
  const [startDateCreated, endDateCreated] = createdDate;
  const [startDateMailing, endDateMailing] = mailingDate;
  const [startDateMailed, endDateMailed] = mailedDate;
  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
  const [mailingDatepicker, setMailingDatepicker] = useState(false);
  const [mailedDatepicker, setMailedDatepicker] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [address, setAddress] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [campaignSearch, setCampaignSearch] = useState('');
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedTemplatesData, setSelectedTemplatesData] = useState([]);

  const dispatch = useDispatch();

  const productTypes = useSelector((state) => state.productReducers.types);
  const sources = useSelector((state) => state.ordersReducers.filterslabels.source);

  const createdBy = useSelector((state) => state.ordersReducers.filterslabels.users);
  const campaignsData = useSelector((state) => state.campaignsReducers.campaigns || []);

  const requiredMetaFields = useSelector(
    (state) => state.ordersReducers.filterslabels.requiredMetaFields
  );

  const campaigns = campaignsData.map((campaign) => ({
    id: campaign.campaignId,
    label: `ID:${campaign.campaignId} ${campaign.name}`,
    ...campaign,
  }));

  // Ensure createdBy is defined before mapping
  // const createdByOptions = createdBy ? createdBy.map(user => ({
  //   label: `${user.title.firstName} (${user.title.email})`,
  //   id: user.title.id
  // })) : [];
  const createdByOptions = useMemo(() => {
    return createdBy
      ? createdBy.map((user) => ({
          label: `${user.title.firstName} (${user.title.email})`,
          id: user.title.id,
        }))
      : [];
  }, [createdBy]);
  const orderStatus = useSelector((state) => state.ordersReducers.filterslabels.orderStatus);

  const paymentStatus = useSelector((state) => state.ordersReducers.filterslabels.paymentStatus);

  const templates = useSelector((state) => state.templateReducer.templates.rows) || [];

  // Merge fetched templates with the selected label cache so that
  // previously-selected templates keep a readable label even when search
  // narrows the results or the page changes.
  const templateOptions = useMemo(() => {
    const base = (templates || []).map((t) => ({
      id: t.id,
      title: `ID:${t.id} ${t.title}`,
    }));
    const baseIds = new Set(base.map((o) => o.id));
    const extras = (selectedTemplatesData || []).filter((t) => !baseIds.has(t.id));
    return [...base, ...extras];
  }, [templates, selectedTemplatesData]);

  const perPage = useSelector(
    (state) => state.ordersReducers.perPage || DEFAULT_PAGINATION.pageSize
  );

  const currentMode = useSelector((state) => state.userReducers.user.isLiveMode || false);

  const filters = useSelector((state) => state.ordersReducers.filters);

  const isAnyFilterApplied = () => {
    return (
      (selectedCreatedBy && selectedCreatedBy?.id) ||
      selectedProductType?.length > 0 ||
      selectedSources?.length > 0 ||
      selectedOrderStatus?.length > 0 ||
      selectedPaymentStatus?.length > 0 ||
      selectedTemplates?.length > 0 ||
      (startDateCreated && endDateCreated) ||
      (startDateMailing && endDateMailing) ||
      (startDateMailed && endDateMailed) ||
      (address && address.trim().length > 0) ||
      Object.keys(metaData)?.length > 0 ||
      selectedCampaign
    );
  };

  const onClose = () => {
    clearFilters(true);
  };

  const clearFilters = (persist = false) => {
    setSelectedCreatedBy([]);
    setSelectedProductType([]);
    setSelectedSources([]);
    setSelectedOrderStatus([]);
    setSelectedPaymentStatus([]);
    setSelectedTemplates([]);
    setSelectedTemplatesData([]);
    setCreatedDate([null, null]);
    setMailingDate([null, null]);
    setMailedDate([null, null]);
    setSelectedCampaign(null);
    setMetaData({});
    setAddress(null);
    !persist ? dispatch({ type: CLEAR_ORDERS_FILTERS }) : undefined;
    handleCloseFilter();
  };

  const applyFilters = () => {
    const createdDate =
      startDateCreated && endDateCreated
        ? {
            startDate: formatDateOnly(startDateCreated),
            endDate: formatDateOnly(endDateCreated),
          }
        : null;

    if (startDateCreated && endDateCreated === null) {
      dispatch(failure(MESSAGES.ORDERS.END_DATE_REQUIRED));
      return;
    }

    const mailingDate =
      startDateMailing && endDateMailing
        ? {
            startDate: formatDateOnly(startDateMailing),
            endDate: formatDateOnly(endDateMailing),
          }
        : null;

    if (startDateMailing && endDateMailing === null) {
      dispatch(failure(MESSAGES.ORDERS.END_DATE_REQUIRED));
      return;
    }

    const mailedDate =
      startDateMailed && endDateMailed
        ? {
            startDate: formatDateOnly(startDateMailed),
            endDate: formatDateOnly(endDateMailed),
          }
        : null;

    if (startDateMailed && endDateMailed === null) {
      dispatch(failure(MESSAGES.ORDERS.END_DATE_REQUIRED));
      return;
    }

    let payload = {
      applyFilters: true,
      filters: {
        createdBy:
          selectedCreatedBy && selectedCreatedBy?.id ? [Number(selectedCreatedBy?.id)] : [],
        productTypes: selectedProductType,
        sources: selectedSources,
        orderStatus: selectedOrderStatus,
        paymentStatus: selectedPaymentStatus,
        templateIds: selectedTemplates,
        ...(selectedTemplates?.length > 0 && { selectedTemplates: selectedTemplatesData }),
        createdDate: createdDate,
        mailingDate: mailingDate,
        mailedDate: mailedDate,
        ...(selectedCampaign && {
          automationId: +selectedCampaign.id,
          campaigns: selectedCampaign,
        }),
        ...(address ? { address } : {}),
        ...(Object.keys(metaData).length > 0 &&
          Object.values(metaData).some((value) => value !== '') && {
            metaData,
          }),
      },
    };

    const filtersWithValues = Object.keys(payload.filters).filter((key) => {
      if (key === 'campaigns' || key === 'selectedTemplates') return false;
      const value = payload.filters[key];
      if (key === 'automationId' && !value) return false;
      return Array.isArray(value) ? value.length > 0 : value !== null;
    });

    payload.filterSize = filtersWithValues.length;

    updatePagination({
      pageSize: perPage,
      page: 1,
    });

    if (filtersWithValues.length <= 0) {
      dispatch(failure(MESSAGES.ORDERS.FILTERS_REQUIRED));
      return;
    }

    dispatch({ type: SET_ORDERS_FILTERS, payload: payload });
    onClose();
  };

  const searching = async () => {
    setLoading(true);
    try {
      if (!search) {
        setFilteredOptions(createdByOptions);
      } else {
        const filtered = createdByOptions.filter((option) =>
          option.label?.toLowerCase()?.includes(search?.toLowerCase())
        );
        setFilteredOptions(filtered);
      }
    } catch (error) {
      console.error('Error filtering users:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCampaigns = async () => {
    try {
      setCampaignLoading(true);
      let payload = {
        pagination: DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE,
        search:
          campaignSearch?.length > 0
            ? campaignSearch?.includes('ID:')
              ? campaignSearch?.replace(/ID:\d+\s*/, '')
              : campaignSearch
            : null,
      };
      await dispatch(fetchCampaigns(payload));
    } catch (error) {
      return error;
    } finally {
      setCampaignLoading(false);
    }
  };

  const updateMetaDataFields = (key, value) => {
    setMetaData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Server-side template search; returns the promise so MultiSelectNew
  // can drive its own loading indicator.
  const searchTemplates = (value) => {
    return dispatch(
      getAllTemplates(
        DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.page,
        DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.pageSize,
        value || ''
      )
    );
  };

  // Keep selected IDs and the label cache in sync so chips/tooltips
  // still display the template name after navigating pages or searching.
  const handleTemplateChange = (ids) => {
    setSelectedTemplates(ids || []);
    setSelectedTemplatesData((prevCache) => {
      const prevById = new Map(prevCache.map((t) => [t.id, t]));
      return (ids || []).map((id) => {
        const cached = prevById.get(id);
        if (cached) return cached;
        const option = templateOptions.find((o) => o.id === id);
        return option ? { id: option.id, title: option.title } : { id, title: `ID:${id}` };
      });
    });
  };

  useImperativeHandle(ref, () => ({
    clearFilters() {
      clearFilters();
    },
  }));

  useEffect(() => {
    clearFilters();
  }, [currentMode]);

  useEffect(() => {
    if (Object.values(filters).length) {
      if (filters.createdBy) {
        const selectedUser = createdBy?.find((user) => user.title.id == filters.createdBy);
        setSelectedCreatedBy(
          selectedUser
            ? {
                id: selectedUser.title.id,
                label: `${selectedUser.title.firstName} (${selectedUser.title.email})`,
              }
            : null
        );
      }
      setMetaData(filters.metaData || {});
      setAddress(filters.address);
      setSelectedProductType(filters.productTypes);
      setSelectedSources(filters.sources);
      setSelectedOrderStatus(filters.orderStatus);
      setSelectedPaymentStatus(filters.paymentStatus);
      setSelectedTemplates(filters.templateIds || []);
      setSelectedTemplatesData(filters.selectedTemplates || []);
      setSelectedCampaign(filters?.campaigns || null);
      filters.createdDate
        ? setCreatedDate([
            parseLocalDateOnly(filters?.createdDate?.startDate),
            parseLocalDateOnly(filters?.createdDate?.endDate),
          ])
        : undefined;
      filters.mailingDate
        ? setMailingDate([
            parseLocalDateOnly(filters?.mailingDate?.startDate),
            parseLocalDateOnly(filters?.mailingDate?.endDate),
          ])
        : undefined;
      filters.mailedDate
        ? setMailedDate([
            parseLocalDateOnly(filters?.mailedDate?.startDate),
            parseLocalDateOnly(filters?.mailedDate?.endDate),
          ])
        : undefined;
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      dispatch(
        getAllTemplates(
          DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.page,
          DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.pageSize
        )
      );
    }
  }, [open]);

  useEffect(() => {
    getCampaigns();
  }, [open, campaignSearch]);

  useEffect(() => {
    searching();
  }, [search, createdByOptions]);

  return (
    <OlcFormModal
      open={open}
      onClose={onClose}
      title={MESSAGES.ORDERS.ADVANCE_FILTERS.HEADING}
      className="ordersFilterModalWrapper"
      contentClassName="ordersFilterContent"
      footer={
        <>
          <OlcButton variant="secondary" onClick={() => clearFilters()}>
            {MESSAGES.ORDERS.ADVANCE_FILTERS.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton variant="primary" onClick={applyFilters} disabled={!isAnyFilterApplied()}>
            {MESSAGES.ORDERS.ADVANCE_FILTERS.SUBMIT_BUTTON}
          </OlcButton>
        </>
      }
    >
      <Box className="ordersFilterTagsWrapper">
        <Grid container className="ordersFilterTagContent" spacing={1}>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="addressMailWrapper">
              <Box className="selectAddress">
                <InputLabel className="addressLabel">
                  {MESSAGES.ORDERS.ADVANCE_FILTERS.PRODUCT_TYPE_LABEL_NEW}
                </InputLabel>
                <MultiSelectNew
                  options={productTypes}
                  selectedValue={selectedProductType}
                  setSelectedValue={setSelectedProductType}
                  placeHolderText={MESSAGES.ORDERS.ADVANCE_FILTERS.PRODUCT_TYPE_LABEL_NEW}
                  noOptionText={MESSAGES.ORDERS.ADVANCE_FILTERS.NO_PRODUCT_TYPE}
                  clearable={true}
                  newDesign={true}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="addressMailWrapper">
              <Box className="selectAddress">
                <InputLabel className="addressLabel">
                  {MESSAGES.ORDERS.ADVANCE_FILTERS.SOURCE_LABEL}
                </InputLabel>
                <MultiSelectNew
                  options={sources}
                  selectedValue={selectedSources}
                  setSelectedValue={setSelectedSources}
                  placeHolderText={MESSAGES.ORDERS.ADVANCE_FILTERS.SOURCE_LABEL}
                  noOptionText={MESSAGES.ORDERS.ADVANCE_FILTERS.NO_SOURCE}
                  clearable={true}
                  newDesign={true}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="addressMailWrapper">
              <Box className="selectAddress">
                <InputLabel className="addressLabel">
                  {MESSAGES.ORDERS.ADVANCE_FILTERS.ORDER_STATUS_LABEL}
                </InputLabel>
                <MultiSelectNew
                  options={orderStatus}
                  selectedValue={selectedOrderStatus}
                  setSelectedValue={setSelectedOrderStatus}
                  placeHolderText={MESSAGES.ORDERS.ADVANCE_FILTERS.ORDER_STATUS_LABEL}
                  noOptionText={MESSAGES.ORDERS.ADVANCE_FILTERS.NO_ORDER_STATUS}
                  clearable={true}
                  newDesign={true}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="addressMailWrapper">
              <Box className="selectAddress">
                <InputLabel className="addressLabel">
                  {MESSAGES.ORDERS.ADVANCE_FILTERS.PAYMENT_STATUS_LABEL}
                </InputLabel>
                <MultiSelectNew
                  options={paymentStatus}
                  selectedValue={selectedPaymentStatus}
                  setSelectedValue={setSelectedPaymentStatus}
                  placeHolderText={MESSAGES.ORDERS.ADVANCE_FILTERS.PAYMENT_STATUS_LABEL}
                  noOptionText={MESSAGES.ORDERS.ADVANCE_FILTERS.NO_PAYMENT_STATUS}
                  clearable={true}
                  newDesign={true}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="addressMailWrapper">
              <Box className="selectAddress">
                <InputLabel className="addressLabel">
                  {MESSAGES.ORDERS.ADVANCE_FILTERS.TEMPLATE_LABEL}
                </InputLabel>
                <MultiSelectNew
                  options={templateOptions}
                  selectedValue={selectedTemplates}
                  setSelectedValue={handleTemplateChange}
                  placeHolderText={MESSAGES.ORDERS.ADVANCE_FILTERS.TEMPLATE_LABEL}
                  noOptionText={MESSAGES.ORDERS.ADVANCE_FILTERS.NO_TEMPLATE}
                  component="multipleIds"
                  onSearchChange={searchTemplates}
                  type="remove"
                  clearable={true}
                  newDesign={true}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="mailedDate newDatePicker updatedDesign">
              <Box className="selectDate">
                <InputLabel className="dateLabel newfieldsLabel">
                  {MESSAGES.ORDERS.ADVANCE_FILTERS.CREATED_DATE_LABEL}
                </InputLabel>
                <img
                  src={MultiSelectArrowCIcon}
                  alt="arrow"
                  style={{ transform: isDatepickerOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
                <DatePicker
                  minDate={null}
                  toggleCalendarOnIconClick={true}
                  selectsRange={true}
                  startDate={startDateCreated}
                  endDate={endDateCreated}
                  onChange={(update) => {
                    dateProtector(update);
                    setCreatedDate(update);
                  }}
                  onChangeRaw={(e) => {
                    if (e.target.value === '' || DATE_REGEX.test(e.target.value)) {
                      handleRawChangeForDatePicker(e, setCreatedDate);
                    } else {
                      e.preventDefault();
                    }
                  }}
                  onCalendarClose={() => setIsDatepickerOpen(false)}
                  onFocus={() => setIsDatepickerOpen(true)}
                  onBlur={(e) => {
                    if (e.target.value == '') {
                      setCreatedDate([null, null]);
                    }
                    setIsDatepickerOpen(false);
                  }}
                  placeholderText={`${MESSAGES.ORDERS.ADVANCE_FILTERS.CREATED_DATE_LABEL}`}
                  isClearable
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="mailedDate newDatePicker updatedDesign">
              <Box className="selectDate">
                <InputLabel className="dateLabel newfieldsLabel">
                  {MESSAGES.ORDERS.ADVANCE_FILTERS.MAILING_DATE_LABEL}
                </InputLabel>
                <img
                  src={MultiSelectArrowCIcon}
                  alt="arrow"
                  style={{
                    transform: mailingDatepicker ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
                <DatePicker
                  minDate={null}
                  onCalendarClose={() => setMailingDatepicker(false)}
                  onFocus={() => setMailingDatepicker(true)}
                  onBlur={(e) => {
                    if (e.target.value == '') {
                      setMailingDate([null, null]);
                    }
                    setIsDatepickerOpen(false);
                  }}
                  toggleCalendarOnIconClick={true}
                  selectsRange={true}
                  startDate={startDateMailing}
                  endDate={endDateMailing}
                  onChange={(update) => {
                    dateProtector(update);
                    setMailingDate(update);
                  }}
                  onChangeRaw={(e) => {
                    if (e.target.value === '' || DATE_REGEX.test(e.target.value)) {
                      handleRawChangeForDatePicker(e, setMailingDate);
                    } else {
                      e.preventDefault();
                    }
                  }}
                  placeholderText={`${MESSAGES.ORDERS.ADVANCE_FILTERS.MAILING_DATE_LABEL}`}
                  isClearable
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="addressMailWrapper">
              <Box className="selectAddress">
                <InputLabel className="addressLabel">
                  {MESSAGES.ORDERS.ADVANCE_FILTERS.ADDRESS_LABEL}
                </InputLabel>
                <Input
                  className="createdByInput newUpdateInput"
                  value={address}
                  name="searchAddress"
                  autoComplete="off"
                  onChange={(e) => setAddress(e.target.value)}
                  type="text"
                  placeholder={MESSAGES.ORDERS.ADVANCE_FILTERS.ADDRESS_PLACEHOLDER}
                  endAdornment={
                    <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                      {address && (
                        <IconButton
                          className="cpClearInputBtn"
                          size="small"
                          onClick={() => {
                            setAddress('');
                          }}
                          edge="end"
                          sx={{ padding: '4px' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  }
                />
              </Box>
            </Box>
          </Grid>
          {requiredMetaFields?.length > 0 &&
            requiredMetaFields.map((metaField, index) => (
              <Grid item lg={6} md={6} sm={12} xs={12} key={index}>
                <Box className="addressMailWrapper">
                  <Box className="selectAddress">
                    <InputLabel className="addressLabel">{metaField?.title}</InputLabel>
                    <Input
                      className="createdByInput newUpdateInput"
                      value={metaData[metaField?.title] || ''}
                      name="searchCreator"
                      onChange={(e) => {
                        updateMetaDataFields(metaField?.title, e.target.value);
                      }}
                      type="text"
                      placeholder={metaField?.title}
                      endAdornment={
                        <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                          {metaData[metaField?.title] && (
                            <IconButton
                              className="cpClearInputBtn"
                              size="small"
                              onClick={() => {
                                updateMetaDataFields(metaField?.title, '');
                              }}
                              edge="end"
                              sx={{ padding: '4px' }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                        </InputAdornment>
                      }
                    />
                  </Box>
                </Box>
              </Grid>
            ))}
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="addressMailWrapper">
              <Box className="selectAddress">
                <InputLabel className="addressLabel">
                  {MESSAGES.ORDERS.ADVANCE_FILTERS.CAMPAIGN}
                </InputLabel>
                <GenericAutocomplete
                  placeholder={MESSAGES.ORDERS.ADVANCE_FILTERS.CAMPAIGN_PLACEHOLDER}
                  options={campaigns}
                  setSelectedValue={setSelectedCampaign}
                  selectedValue={selectedCampaign}
                  search={setCampaignSearch}
                  updateErrors={() => {}}
                  name={'campaign'}
                  loading={campaignLoading}
                  filterAutoComplete
                  className="newAutocompleteDesign"
                  newDesign={true}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="addressMailWrapper">
              <Box className="selectAddress">
                <InputLabel className="addressLabel">
                  {MESSAGES.ORDERS.ADVANCE_FILTERS.CREATED_BY_LABEL}
                </InputLabel>
                <GenericAutocomplete
                  placeholder={MESSAGES.ORDERS.ADVANCE_FILTERS.CREATED_BY_PLACEHOLDER}
                  options={filteredOptions}
                  selectedValue={selectedCreatedBy}
                  setSelectedValue={setSelectedCreatedBy}
                  search={setSearch}
                  updateErrors={() => {}}
                  loading={loading}
                  filterAutoComplete
                  className="newAutocompleteDesign"
                  newDesign={true}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <Box className="mailedDate newDatePicker updatedDesign">
              <Box className="selectDate">
                <InputLabel className="dateLabel newfieldsLabel">
                  {MESSAGES.ORDERS.ADVANCE_FILTERS.MAILED_DATE_LABEL}
                </InputLabel>
                <img
                  src={MultiSelectArrowCIcon}
                  alt="arrow"
                  style={{ transform: mailedDatepicker ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
                <DatePicker
                  minDate={null}
                  onCalendarClose={() => setMailedDatepicker(false)}
                  onFocus={() => setMailedDatepicker(true)}
                  onBlur={(e) => {
                    if (e.target.value == '') {
                      setMailedDate([null, null]);
                    }
                    setIsDatepickerOpen(false);
                  }}
                  toggleCalendarOnIconClick={true}
                  selectsRange={true}
                  startDate={startDateMailed}
                  endDate={endDateMailed}
                  onChange={(update) => {
                    dateProtector(update);
                    setMailedDate(update);
                  }}
                  onChangeRaw={(e) => {
                    if (e.target.value === '' || DATE_REGEX.test(e.target.value)) {
                      handleRawChangeForDatePicker(e, setMailedDate);
                    } else {
                      e.preventDefault();
                    }
                  }}
                  placeholderText={`${MESSAGES.ORDERS.ADVANCE_FILTERS.MAILED_DATE_LABEL}`}
                  isClearable
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </OlcFormModal>
  );
});

export default AdvanceFilters;
